import {FC, useEffect, useMemo, useState} from "react";
import {Button, CircularProgress, Dialog, DialogActions, IconButton, Table, TableBody} from "@mui/material";
import {defineMessage, FormattedMessage} from "react-intl";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import ErrorHelper from "../../../../../utils/ErrorHelper";
import FormattedTableHead from "../../../../FormattedTableHead";
import Row, {MIN_BUTTON_POSITION} from "./Row";
import {PhoneButtonType, PhoneButton} from "@/api";
import ButtonsForm from "./ButtonsForm";
import {msg_confirm_cancel} from "@dvrt/graphql-widgets";
import {useBoolState, ConfirmDialog, FormattedAlert, DialogContent, FormattedDialogTitle} from "@dvrt/graphql-widgets";
import {
    SavePhoneButtonsMutationVariables,
    useButtonsFormDataQuery,
    useTargetNumbersQuery,
} from "../../queries.generated";
import {DndContext, DragEndEvent} from "@dnd-kit/core";
import {restrictToParentElement, restrictToVerticalAxis} from "@dnd-kit/modifiers";
import {SortableContext, verticalListSortingStrategy} from "@dnd-kit/sortable";
import {moveItemsInArray} from "@/tools";
import {createTargets, Target, TargetType} from "@/components/TargetSelector";
import {ContextFilters, useAuth} from "@/context/AuthProvider";
import {useDomainNameQuery} from "@/hooks";
import ReadonlyTable from "@/components/ReadonlyTable";
import {commonTranslations, PHONE_BUTTON_TYPE_MESSAGES} from "@/utils/translations";

type BaseProps = {
    isOpen: boolean;
    close: () => void;
    provisionedDeviceId: number;
};

export type Props = {
    domainId: number;
    userId: number;
    openResyncPrompt?: () => void;
} & BaseProps;

type ContentProps = {
    initialData: SavePhoneButtonsMutationVariables;
    targetNumbers: Target[];
    close: () => void;
    provisionedDeviceId: number;
    openResyncPrompt?: () => void;
};

const FormTable = ({close, provisionedDeviceId, initialData, targetNumbers, openResyncPrompt}: ContentProps) => {
    const getIdArray = (length: number) => Array.from({length}, (_, i) => i + 1);
    const [isCancelOpen, openCancel, closeCancel] = useBoolState();
    const [buttonSortIds, setButtonSortIds] = useState<number[]>(() => getIdArray(initialData?.buttons?.length ?? 0));

    const form = new ButtonsForm(initialData, `buttons_${provisionedDeviceId}`);

    useEffect(() => {
        form.newInitialData(initialData);
        form.setCacheKeyPostfix(`buttons_${provisionedDeviceId}`);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialData, provisionedDeviceId]);

    useEffect(() => {
        setButtonSortIds(getIdArray(form.values.buttons?.length ?? 0));
    }, [form.values.buttons]);

    const handleDragEnd = (event: DragEndEvent) => {
        const {active, over} = event;

        if (active.id !== (over?.id ?? active.id)) {
            const buttonsCopy = structuredClone(form.values.buttons);
            const oldIndex = buttonSortIds.indexOf(active.id as number);
            const newIndex = buttonSortIds.indexOf(over.id as number);

            const newItems = moveItemsInArray(buttonsCopy, oldIndex, newIndex);

            // also reassign buttonPosition, because we do not want to mess that up
            for (let i = 0; i < newItems.length; i++) {
                newItems[i].buttonPosition = i + MIN_BUTTON_POSITION;
            }

            form.updateValues({buttons: newItems});
            setButtonSortIds(moveItemsInArray(buttonSortIds, oldIndex, newIndex));
        }
    };

    const errorHelper = new ErrorHelper(form.errors);

    function cancel() {
        close();
        form.reset();
    }

    function addButton() {
        const buttons = form.values.buttons?.length ? [...form.values.buttons] : [];
        const button: PhoneButton = {
            buttonType: PhoneButtonType.Empty,
            buttonPosition: buttons.length + MIN_BUTTON_POSITION,
        };
        buttons.push(button);
        form.updateValues({buttons});
    }

    async function submit() {
        if (await form.submit()) {
            close();
            if (openResyncPrompt) openResyncPrompt();
        }
    }

    function createRows() {
        if (!form.values.buttons?.length) return null;
        return form.values.buttons.map((button, index) => (
            <Row
                key={buttonSortIds[index]}
                errorHelper={errorHelper}
                index={index}
                sortId={buttonSortIds[index]}
                targetNumbers={targetNumbers}
                button={button}
                onChange={(values) => {
                    if ("buttonType" in values) form.clearErrors();

                    const formButtons = structuredClone(form.values.buttons);
                    const formButton = formButtons[index];
                    const newValues = {...formButton, ...values};
                    formButtons[index] = {...formButton, ...newValues};
                    form.updateValues({buttons: formButtons});
                }}
                onRemove={() => {
                    const formButtons = structuredClone(form.values.buttons);
                    formButtons.splice(index, 1);
                    form.updateValues({buttons: formButtons});
                }}
            />
        ));
    }

    return (
        <>
            <DialogContent>
                <Table>
                    <FormattedTableHead
                        cells={[
                            {label: defineMessage({id: "hashmark", defaultMessage: "#"})},
                            {label: defineMessage({id: "action", defaultMessage: "Action"})},
                            {label: defineMessage({id: "number", defaultMessage: "Number"})},
                            {label: defineMessage({id: "display", defaultMessage: "Display"})},
                            {
                                renderer: () => (
                                    <IconButton color="primary" size="small" onClick={addButton}>
                                        <FontAwesomeIcon icon="plus-square" />
                                    </IconButton>
                                ),
                            },
                        ]}
                    />
                    <DndContext onDragEnd={handleDragEnd} modifiers={[restrictToVerticalAxis, restrictToParentElement]}>
                        <SortableContext items={buttonSortIds} strategy={verticalListSortingStrategy}>
                            <TableBody id="table-body-1">{createRows()}</TableBody>
                        </SortableContext>
                    </DndContext>
                </Table>
                {!form.values.buttons?.length && (
                    <div style={{padding: "1em", fontStyle: "italic"}}>
                        <FormattedMessage id="no_phone_buttons" defaultMessage="No existing phone button" />
                    </div>
                )}
                {form.values.buttons?.length > 1 && (
                    <FormattedAlert
                        severity="info"
                        message={defineMessage({
                            id: "drag_hint",
                            defaultMessage: "Drag rows to change order",
                        })}
                        containerStyle={{marginTop: 10}}
                    />
                )}
                {errorHelper.hasError("limit") && (
                    <p style={{color: "red"}}>
                        <FormattedMessage id="quota_depleted" defaultMessage="Quota depleted" />
                    </p>
                )}
            </DialogContent>
            <DialogActions>
                <Button disabled={!form.hasChanged} onClick={submit} color="primary" variant="text">
                    <FormattedMessage id="save" defaultMessage="Save" />
                </Button>
                <Button onClick={form.hasChanged ? openCancel : cancel} color="secondary" variant="text">
                    <FormattedMessage id="cancel" defaultMessage="Cancel" />
                </Button>
                <ConfirmDialog
                    isOpen={isCancelOpen}
                    close={closeCancel}
                    onConfirm={() => {
                        close();
                        cancel();
                    }}
                    message={msg_confirm_cancel}
                    buttonIdPrefix="confirm-cancel"
                />
            </DialogActions>
        </>
    );
};

const SaveDialog = ({isOpen, close, provisionedDeviceId, domainId, userId, openResyncPrompt}: Props) => {
    const buttonsQuery = useButtonsFormDataQuery({
        variables: {provisionedDeviceId},
        fetchPolicy: "no-cache",
        skip: !isOpen,
    });

    const targetNumbersQuery = useTargetNumbersQuery({
        variables: {domainId, notUser: userId},
        fetchPolicy: "no-cache",
        skip: !isOpen,
    });

    let content = null;

    if (isOpen) {
        if (buttonsQuery.loading || targetNumbersQuery.loading) {
            // Show loading spinner while data is being fetched
            content = (
                <DialogContent>
                    <CircularProgress />
                </DialogContent>
            );
        } else if (buttonsQuery.error || targetNumbersQuery.error) {
            // Show error message if there was an error fetching data
            content = (
                <DialogContent>
                    <FormattedAlert
                        severity="error"
                        message={defineMessage({
                            id: "error_fetching_phone_buttons",
                            defaultMessage: "Could not fetch phone buttons data",
                        })}
                    />
                </DialogContent>
            );
        } else if (buttonsQuery.data && targetNumbersQuery.data) {
            // Show the form table with fetched data
            content = (
                <FormTable
                    initialData={{
                        provisionedDeviceId,
                        buttons: buttonsQuery.data.phoneButtons,
                    }}
                    targetNumbers={createTargets(targetNumbersQuery.data.users.items, TargetType.EXTENSION)}
                    close={close}
                    provisionedDeviceId={provisionedDeviceId}
                    openResyncPrompt={openResyncPrompt}
                />
            );
        }
    } else {
        content = null;
    }

    return (
        <Dialog open={isOpen} maxWidth="lg">
            <FormattedDialogTitle
                title={defineMessage({id: "manage_phone_buttons", defaultMessage: "Manage phone buttons"})}
            />
            {content}
        </Dialog>
    );
};

type InfoDialogProps = BaseProps;

const InfoDialog: FC<InfoDialogProps> = ({provisionedDeviceId, isOpen, close}) => {
    const {data, loading} = useButtonsFormDataQuery({
        variables: {provisionedDeviceId},
        fetchPolicy: "no-cache",
        skip: !isOpen,
    });

    const tableData = useMemo(() => {
        return (
            data?.phoneButtons.map((button) => ({
                position: button.buttonPosition,
                type: button.buttonType,
                number: button.targetNumberId ?? button.speedDialNumber ?? "",
                display: button.display ?? "",
            })) || []
        );
    }, [data?.phoneButtons]);

    return (
        <Dialog open={isOpen} onClose={close} maxWidth="sm" fullWidth>
            <FormattedDialogTitle
                title={commonTranslations.devices.provisioned.phoneButtons}
                closingButton
                onClose={close}
            />
            <DialogContent>
                {loading ? (
                    <CircularProgress />
                ) : (
                    <ReadonlyTable
                        headers={{
                            position: commonTranslations["#"],
                            type: commonTranslations.action,
                            number: commonTranslations.number,
                            display: commonTranslations.display,
                        }}
                        rows={tableData}
                        renderers={{
                            type: (value) => <FormattedMessage {...PHONE_BUTTON_TYPE_MESSAGES[value]} />,
                        }}
                    />
                )}
            </DialogContent>
        </Dialog>
    );
};

const SaveOrInfoDialog: FC<Props> = ({isOpen, close, provisionedDeviceId, domainId, userId, openResyncPrompt}) => {
    const {domainName} = useDomainNameQuery(domainId);
    const auth = useAuth();

    if (auth.hasPermission("manage_provisioned_device_buttons", ContextFilters.domain(domainName))) {
        return (
            <SaveDialog
                isOpen={isOpen}
                close={close}
                provisionedDeviceId={provisionedDeviceId}
                domainId={domainId}
                userId={userId}
                openResyncPrompt={openResyncPrompt}
            />
        );
    }

    return <InfoDialog provisionedDeviceId={provisionedDeviceId} isOpen={isOpen} close={close} />;
};

export default SaveOrInfoDialog;
